import { useState } from 'react'
import { Plus, Target } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useGoalMutations, useGoals } from '../api'
import {
  CONFIDENCE_LABELS, GOAL_LEVEL_LABELS, GOAL_STATUS_LABELS, MEASURE_LABELS,
} from '../types'
import type { GoalLevel, GoalStatus, MeasureType } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<GoalStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  ACTIVE: 'info',
  ACHIEVED: 'success',
  PARTIALLY_ACHIEVED: 'warning',
  MISSED: 'danger',
  CANCELLED: 'neutral',
}

/** Goals and their alignment. Progress is null until somebody checks in — not zero. */
export function GoalsPage() {
  useBreadcrumb()
  const goals = useGoals()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { save, checkIn } = useGoalMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; employeeId: string; parentGoalId: string; title: string
    goalLevel: GoalLevel; measureType: MeasureType; targetValue: string; unit: string
    startDate: string; dueDate: string
  } | null>(null)
  const [checkinDraft, setCheckinDraft] = useState<{
    goalId: string; currentValue: string; progressPercent: string; confidence: string
    note: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Goals"
        subtitle="What people are working towards, and what it contributes to."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '', employeeId: '', parentGoalId: '',
                title: '', goalLevel: 'INDIVIDUAL', measureType: 'PERCENT',
                targetValue: '', unit: '', startDate: '', dueDate: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New goal
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {goals.isPending && <Skeleton className="m-4 h-24" />}
        {goals.isError && <ErrorState error={goals.error} onRetry={() => void goals.refetch()} />}
        {goals.isSuccess && goals.data.length === 0 && (
          <EmptyState
            title="No goal has been set"
            description="A goal can belong to a person, or to the company with others aligned beneath it."
          />
        )}
        {goals.isSuccess && goals.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Goal</th>
                  <th className="p-3 text-start font-medium">Owner</th>
                  <th className="p-3 text-start font-medium">Level</th>
                  <th className="p-3 text-start font-medium">Contributes to</th>
                  <th className="p-3 text-end font-medium">Progress</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {goals.data.map((goal) => (
                  <tr key={goal.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {goal.title}
                      {goal.childCount > 0 && (
                        <span className="block text-xs text-slate-600">
                          {goal.childCount} goal(s) aligned to it
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {goal.employeeName ?? 'The company'}
                    </td>
                    <td className="p-3 text-slate-600">
                      {GOAL_LEVEL_LABELS[goal.goalLevel as GoalLevel] ?? goal.goalLevel}
                    </td>
                    <td className="p-3 text-slate-600">{goal.parentGoalTitle ?? '—'}</td>
                    <td className="p-3 text-end">
                      {goal.progressPercent == null ? (
                        <span className="text-xs text-slate-600">Not started</span>
                      ) : (
                        <span className="font-medium">{goal.progressPercent}%</span>
                      )}
                      {goal.checkinCount > 0 && (
                        <span className="block text-xs text-slate-600">
                          {goal.checkinCount} check-in(s)
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={goal.status}
                        tone={STATUS_TONE[goal.status as GoalStatus] ?? 'neutral'}
                        label={GOAL_STATUS_LABELS[goal.status as GoalStatus] ?? goal.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setError(null)
                          setCheckinDraft({
                            goalId: goal.id, currentValue: '', progressPercent: '',
                            confidence: '', note: '',
                          })
                        }}
                      >
                        Check in
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Target className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A goal nobody has updated reads "Not started", not 0%. The two are different facts, and
        a dashboard that shows the second manufactures a problem out of silence.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="New goal"
        description="Aligning it to another goal is optional; an alignment that loops back on itself is refused."
        submitLabel="Save goal"
        loading={save.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await save.mutateAsync({
                legalEntityId: draft.legalEntityId,
                employeeId: draft.employeeId || null,
                parentGoalId: draft.parentGoalId || null,
                title: draft.title,
                goalLevel: draft.goalLevel,
                measureType: draft.measureType,
                targetValue: draft.targetValue ? Number(draft.targetValue) : null,
                unit: draft.unit || null,
                startDate: draft.startDate || null,
                dueDate: draft.dueDate || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That goal could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="goal-title">Goal</Label>
              <Input
                id="goal-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="goal-owner">Owner</Label>
              <EmployeePicker
                  id="goal-owner"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="goal-level">Level</Label>
              <Select
                id="goal-level"
                className="w-full"
                value={draft.goalLevel}
                onChange={(event) =>
                  setDraft({ ...draft, goalLevel: event.target.value as GoalLevel })}
              >
                {Object.entries(GOAL_LEVEL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="goal-parent">Contributes to</Label>
              <Select
                id="goal-parent"
                className="w-full"
                value={draft.parentGoalId}
                onChange={(event) => setDraft({ ...draft, parentGoalId: event.target.value })}
              >
                <option value="">Nothing above it</option>
                {goals.data?.map((goal) => (
                  <option key={goal.id} value={goal.id}>{goal.title}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="goal-measure">Measured by</Label>
              <Select
                id="goal-measure"
                className="w-full"
                value={draft.measureType}
                onChange={(event) =>
                  setDraft({ ...draft, measureType: event.target.value as MeasureType })}
              >
                {Object.entries(MEASURE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            {draft.measureType === 'NUMERIC' && (
              <>
                <div>
                  <Label htmlFor="goal-target">Target</Label>
                  <Input
                    id="goal-target" type="number" value={draft.targetValue}
                    onChange={(event) =>
                      setDraft({ ...draft, targetValue: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Required: without it every check-in would be a guess.
                  </p>
                </div>
                <div>
                  <Label htmlFor="goal-unit">Unit</Label>
                  <Input
                    id="goal-unit" value={draft.unit}
                    onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
                  />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="goal-start">From</Label>
              <DatePicker
                id="goal-start" value={draft.startDate}
                onChange={(event) => setDraft({ ...draft, startDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="goal-due">Due</Label>
              <DatePicker
                id="goal-due" value={draft.dueDate}
                onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={checkinDraft != null}
        onOpenChange={(open) => { if (!open) setCheckinDraft(null) }}
        title="Check in"
        description="Confidence matters as much as the number: a percentage rising steadily can still be off track against the date."
        submitLabel="Record"
        loading={checkIn.isPending}
        error={error}
        onSubmit={() => {
          if (!checkinDraft) return
          void (async () => {
            setError(null)
            try {
              await checkIn.mutateAsync({
                id: checkinDraft.goalId,
                body: {
                  currentValue: checkinDraft.currentValue
                    ? Number(checkinDraft.currentValue) : null,
                  progressPercent: checkinDraft.progressPercent
                    ? Number(checkinDraft.progressPercent) : null,
                  confidence: checkinDraft.confidence || null,
                  note: checkinDraft.note || null,
                },
              })
              setCheckinDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That check-in could not be recorded')
            }
          })()
        }}
      >
        {checkinDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="checkin-progress">Progress, %</Label>
              <Input
                id="checkin-progress" type="number" value={checkinDraft.progressPercent}
                onChange={(event) =>
                  setCheckinDraft({ ...checkinDraft, progressPercent: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="checkin-value">Current value</Label>
              <Input
                id="checkin-value" type="number" value={checkinDraft.currentValue}
                onChange={(event) =>
                  setCheckinDraft({ ...checkinDraft, currentValue: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="checkin-confidence">Confidence</Label>
              <Select
                id="checkin-confidence"
                className="w-full"
                value={checkinDraft.confidence}
                onChange={(event) =>
                  setCheckinDraft({ ...checkinDraft, confidence: event.target.value })}
              >
                <option value="">Not stated</option>
                {Object.entries(CONFIDENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="checkin-note">Note</Label>
              <Input
                id="checkin-note" value={checkinDraft.note}
                onChange={(event) =>
                  setCheckinDraft({ ...checkinDraft, note: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
